import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../../shared/api/firebase/config';
import { ASSETS, LAW_FIRM_INFO } from '../../config/Config';
import { PortalProvider, usePortalContext } from '../../context/PortalContext';
import ConsentGate from '../portal/ConsentGate';
import WorkspaceSelector from '../portal/WorkspaceSelector';
import PortalCompletarCadastro from '../../pages/portal/PortalCompletarCadastro';
import MfaEnrollment from '../portal/MfaEnrollment';
import MfaGate from '../portal/MfaGate';
import ProfileSelector from '../../pages/portal/ProfileSelector';
import { isRegistrationComplete } from '../../utils/registrationUtils';
import InstallPWABanner from '../portal/InstallPWABanner';
import OnboardingTour from '../portal/OnboardingTour';
import NotificationBell from '../portal/NotificationBell';
import { usePortalNotifications } from '../../hooks/usePortalNotifications';

function MfaController({ children }) {
  const { crmClient } = usePortalContext();

  // Zero-Trust: a sessão é vinculada ao ID do cliente, não a um booleano genérico.
  // Se o usuário trocar de perfil (Efeito Netflix), o ID armazenado não bate → Gate bloqueia.
  const SESSION_KEY = 'mfaVerifiedClient';

  const isVerifiedForCurrentClient = () =>
 {
    try { return sessionStorage.getItem(SESSION_KEY) === (crmClient?.id || ''); } catch { return false; }
  };

  const [verified, setVerified] = useState(() => isVerifiedForCurrentClient());

  // Re-avalia sempre que crmClient mudar (troca de perfil)
  useEffect(() => {
    setVerified(isVerifiedForCurrentClient());
  }, [crmClient?.id]);

  const handleVerified = () => {
    try { sessionStorage.setItem(SESSION_KEY, crmClient?.id || ''); } catch {}
    setVerified(true);
  };

  if (!crmClient) return children;

  const mfaEnabled = Boolean(crmClient?.security?.mfaEnabled);
  const pinHash    = crmClient?.security?.pinHash || '';
  const mfaSecret  = crmClient?.security?.mfaSecret || ''; // legado TOTP

  if (!verified && (!mfaEnabled || (!pinHash && !mfaSecret))) {
    return <MfaEnrollment onEnrolled={handleVerified} />;
  }

  if (!verified) {
    return <MfaGate pinHash={pinHash} secret={mfaSecret} onVerified={handleVerified} />;
  }

  return children;
}

function HardLockGate({ activeKey, onNavigate, children, forcedClientId }) {
  const { crmClient, loading, profileCandidates, setProfileCandidates, setHumanProfile, setCrmClient } = usePortalContext();

  if (profileCandidates && profileCandidates.length > 1) {
    return (
      <PortalLayoutInner activeKey={activeKey} onNavigate={onNavigate} sidebarLocked={true}>
        <ProfileSelector
          profiles={profileCandidates}
          onSelect={(perfil) => {
            setHumanProfile(perfil);
            setCrmClient(perfil);
            setProfileCandidates(null);
          }}
        />
      </PortalLayoutInner>
    );
  }

  const locked = !loading && crmClient && !isRegistrationComplete(crmClient);

  if (locked) {
    return (
      <PortalLayoutInner activeKey={activeKey} onNavigate={onNavigate} sidebarLocked={true}>
        <PortalCompletarCadastro embedded={true} clientId={crmClient?.id} />
      </PortalLayoutInner>
    );
  }

  return (
    <PortalLayoutInner activeKey={activeKey} onNavigate={onNavigate}>
      {forcedClientId ? children : <MfaController>{children}</MfaController>}
    </PortalLayoutInner>
  );
}

const NAV_ITEMS = [
  { key: 'dashboard',  label: 'Início',      path: '/portal/dashboard',   iconClass: 'fas fa-home'     },
  { key: 'caso',       label: 'Meu Caso',    path: '/portal/caso',         iconClass: 'fas fa-briefcase'},
  { key: 'financeiro', label: 'Financeiro',  path: '/portal/financeiro',   iconClass: 'fas fa-coins'    },
];

const PERFIL_ITEM = { key: 'perfil', label: 'Minha Conta', path: '/portal/perfil', iconClass: 'fas fa-user-cog' };

const ALL_ITEMS = [...NAV_ITEMS, PERFIL_ITEM];

function NavItem({ item, active, onClick, badge = 0 }) {
  const { label, iconClass } = item;
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'group w-full text-left flex items-center gap-3 px-3 py-2 rounded-md relative overflow-hidden transition-colors ' +
        (active
          ? 'bg-[#C5A059] text-[#20243B] shadow-md font-bold'
          : 'text-gray-400 hover:bg-white/5 hover:text-white')
      }
    >
      <div className="relative shrink-0">
        <i
          className={`${iconClass} w-3.5 text-center ${active ? 'text-[#20243B]' : 'text-gray-400 group-hover:text-[#C5A059]'}`}
          aria-hidden="true"
        />
        {badge > 0 && (
          <span className="absolute -top-1.5 -right-2 h-3.5 min-w-3.5 px-0.5 rounded-full bg-red-500 text-white text-[8px] font-bold flex items-center justify-center leading-none">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </div>
      <span className="text-xs font-medium truncate flex-1">{label}</span>
      {active && (
        <i className="fas fa-chevron-right text-[10px] opacity-50 text-[#20243B]" aria-hidden="true" />
      )}
    </button>
  );
}


function PortalLayoutInner({ activeKey, onNavigate, children, sidebarLocked = false }) {
  const navigate = useNavigate();
  const safeNavigate = (path, key) => {
    if (!path) return;
    if (typeof onNavigate === 'function') { onNavigate(key); return; }
    navigate(path);
  };

  const handleSignOut = async () => {
    try { sessionStorage.removeItem('mfaVerifiedClient'); } catch {}
    try { sessionStorage.clear(); } catch {}
    try { await signOut(auth); } catch {}
    window.location.replace('/');
  };

  const { crmClient, isPartner } = usePortalContext();
  const { total: notifTotal, docCount, taskCount, invoiceCount } = usePortalNotifications();

  const toTitleCase = (s) => String(s).toLowerCase().replace(/(?:^|\s|-)\S/g, c => c.toUpperCase());

  const email = auth?.currentUser?.email ? String(auth.currentUser.email) : null;
  const displayName = useMemo(() => toTitleCase(String(
    crmClient?.fullName ||
    crmClient?.qualification?.physical?.socialName ||
    crmClient?.qualification?.physical?.name ||
    crmClient?.contact?.name ||
    crmClient?.name ||
    (email ? email.split('@')[0] : '') ||
    'Cliente'
  ).trim()), [crmClient, email]);
  const displayEmail = String(crmClient?.contact?.email || crmClient?.email || email || '').trim();

  return (
    <div className="flex h-full bg-white font-sans overflow-hidden">

      {/* ─── SIDEBAR DESKTOP ─── */}
      <aside className={`rda-sidebar hidden md:flex w-64 bg-[#1a1e30] text-white flex-col h-full flex-shrink-0 print:hidden z-30 shadow-2xl border-r border-gray-800 transition-all duration-300${sidebarLocked ? ' opacity-40 grayscale pointer-events-none select-none' : ''}`}>

        {/* Logo bar */}
        <div className="h-12 flex items-center px-4 border-b border-gray-700/50 bg-[#20243B] flex-shrink-0">
          <button
            type="button"
            onClick={() => safeNavigate('/portal/dashboard', 'dashboard')}
            className="flex items-center gap-2 md:gap-3 hover:opacity-90 transition-opacity group"
            title="Início"
          >
            <img src={ASSETS.logoIcon} alt="Logo" className="h-7 w-auto" />
            <img src={ASSETS.logoName} alt="RDA" className="w-auto mt-0.5 h-[12px]" />
          </button>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto overscroll-contain py-3 px-3 custom-scrollbar space-y-0.5">
          {NAV_ITEMS.map((item) => {
            const badge =
              item.key === 'dashboard'  ? notifTotal :
              item.key === 'caso'       ? docCount :
              item.key === 'financeiro' ? invoiceCount :
              item.key === 'vault'      ? taskCount :
              0;
            return (
              <NavItem
                key={item.key}
                item={item}
                active={item.key === activeKey}
                onClick={() => safeNavigate(item.path, item.key)}
                badge={badge}
              />
            );
          })}
          {/* Workspaces (Meus Representados) */}
          <WorkspaceSelector />
        </nav>

        {/* Footer: perfil + logout */}
        <div className="flex-shrink-0 border-t border-gray-700/50 bg-black/20 px-2 py-2 space-y-1.5">
          <button
            type="button"
            onClick={() => safeNavigate(PERFIL_ITEM.path, PERFIL_ITEM.key)}
            className={`w-full flex items-center gap-3 px-2.5 py-2 rounded-lg transition-all text-left border group ${activeKey === 'perfil' ? 'bg-[#C5A059]/20 border-[#C5A059]/60 shadow-sm' : 'bg-white/5 border-white/10 hover:bg-white/10 hover:border-[#C5A059]/40'}`}
            title="Clique para abrir Minha Conta"
          >
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white border-2 shadow-inner shrink-0 transition-colors ${activeKey === 'perfil' ? 'bg-[#C5A059] border-[#C5A059]' : 'bg-gradient-to-br from-gray-600 to-gray-800 border-gray-500 group-hover:border-[#C5A059]'}`}>
              <i className={`fas fa-user text-[11px] ${activeKey === 'perfil' ? 'text-[#20243B]' : 'text-slate-200'}`} aria-hidden="true" />
            </div>
            <div className="overflow-hidden flex-1 min-w-0">
              <p className={`text-[10px] font-bold uppercase tracking-wider leading-none ${activeKey === 'perfil' ? 'text-[#C5A059]' : 'text-[#C5A059]/70 group-hover:text-[#C5A059]'}`}>Minha Conta</p>
              <p className="text-xs font-medium text-white truncate leading-tight mt-0.5" title={displayName}>{displayName}</p>
              <p className="text-[10px] text-gray-400 truncate leading-tight" title={displayEmail}>{displayEmail}</p>
            </div>
            <i className={`fas fa-chevron-right text-[10px] transition-colors ${activeKey === 'perfil' ? 'text-[#C5A059]' : 'text-gray-500 group-hover:text-[#C5A059]'}`} aria-hidden="true" />
          </button>

          {/* Cartão do advogado — Art.327: oculto para PARTNER (são eles mesmos advogados) */}
          {!isPartner && (
            <a
              href={`${LAW_FIRM_INFO.contact.whatsappLink}?text=${encodeURIComponent('Olá! Sou cliente do escritório e preciso falar sobre meu caso.')}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg bg-white/5 border border-white/10 hover:border-[#C5A059]/40 hover:bg-white/10 transition-colors group"
              title="Falar com o advogado"
            >
              <div className="w-8 h-8 rounded-full bg-[#C5A059]/20 border border-[#C5A059]/40 flex items-center justify-center shrink-0 overflow-hidden">
                {LAW_FIRM_INFO.contact.photoURL ? (
                  <img src={LAW_FIRM_INFO.contact.photoURL} alt={LAW_FIRM_INFO.name} className="w-full h-full object-cover" />
                ) : (
                  <i className="fas fa-user-tie text-[10px] text-[#C5A059]" aria-hidden="true" />
                )}
              </div>
              <div className="overflow-hidden flex-1 min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#C5A059]/70 leading-none">Seu Advogado</p>
                <p className="text-xs font-medium text-white truncate mt-0.5 leading-tight">{LAW_FIRM_INFO.name}</p>
                <p className="text-[10px] text-gray-400 leading-tight">{LAW_FIRM_INFO.oab}</p>
              </div>
              <i className="fab fa-whatsapp text-[#25D366] text-sm group-hover:scale-110 transition-transform shrink-0" aria-hidden="true" />
            </a>
          )}

          <button
            type="button"
            onClick={handleSignOut}
            className="w-full bg-red-500/10 text-red-400 border border-red-500/20 py-1.5 rounded hover:bg-red-600 hover:text-white text-[11px] font-bold flex items-center justify-center gap-2 group transition-colors"
          >
            <i className="fas fa-sign-out-alt text-[10px]" aria-hidden="true" />
            Sair
          </button>
        </div>
      </aside>

      {/* ─── MAIN ─── */}
      <main className="flex-1 flex flex-col h-full overflow-hidden relative bg-gray-50 print:h-auto print:overflow-visible">

        {/* Mobile top bar */}
        <header className="bg-white border-b border-gray-200 h-12 flex items-center px-3 justify-between print:hidden flex-shrink-0 sticky top-0 z-20 shadow-sm md:hidden">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded bg-[#20243B] flex items-center justify-center">
              <span className="text-[#C5A059] font-extrabold text-[10px]">RDA</span>
            </div>
            <span className="text-xs font-extrabold text-[#20243B]">Portal</span>
          </div>

          <div className="flex items-center gap-1 overflow-x-auto">
            {ALL_ITEMS.map(({ key, label, path, iconClass }) => {
              const active = key === activeKey;
              const itemBadge = 0;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => safeNavigate(path, key)}
                  className={
                    'relative h-7 w-7 rounded border flex items-center justify-center shrink-0 ' +
                    (active
                      ? 'bg-[#C5A059] text-[#20243B] border-[#C5A059]'
                      : 'bg-white text-gray-500 border-gray-200')
                  }
                  title={label}
                >
                  <i className={`${iconClass} text-xs`} aria-hidden="true" />
                  {itemBadge > 0 && (
                    <span className="absolute -top-1 -right-1 h-3.5 min-w-3.5 px-0.5 rounded-full bg-red-500 text-white text-[8px] font-bold flex items-center justify-center leading-none">
                      {itemBadge > 9 ? '9+' : itemBadge}
                    </span>
                  )}
                </button>
              );
            })}
            <NotificationBell onNavigate={(key) => { const item = ALL_ITEMS.find(i => i.key === key); if (item) safeNavigate(item.path, key); }} />
            <button
              type="button"
              onClick={handleSignOut}
              className="h-7 w-7 rounded border border-gray-200 bg-white text-red-500 flex items-center justify-center shrink-0"
              title="Sair"
            >
              <i className="fas fa-sign-out-alt text-xs" aria-hidden="true" />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto relative flex flex-col custom-scrollbar p-3 md:p-6 bg-gray-50">
          <div className="w-full h-full flex flex-col pb-16">
            <ConsentGate>{children}</ConsentGate>
          </div>
        </div>
      </main>

      {/* A4 — PWA install banner (mobile only, once) */}
      <InstallPWABanner />

      {/* C4 — Onboarding guided tour (first login only) */}
      <OnboardingTour />
    </div>
  );
}

export default function PortalLayout({ activeKey, onNavigate, children, forcedClientId = null }) {
  return (
    <PortalProvider forcedClientId={forcedClientId}>
      <HardLockGate activeKey={activeKey} onNavigate={onNavigate} forcedClientId={forcedClientId}>
        {children}
      </HardLockGate>
    </PortalProvider>
  );
}
